import React, { useMemo, useState } from 'react';
import type { MedicationRecord, DrugRecord, DripRecord, WizardState, DripCalcType, DrugType } from '../types';
import type { Action } from '../state/configReducer';
import { getProtocolsForMedication } from '../state/configReducer';
import CasesEditor from './CasesEditor';
import MedicationPreview from './MedicationPreview';
import ManageProtocolsModal from './ManageProtocolsModal';

interface Props {
  medication: MedicationRecord;
  state: WizardState;
  hospital: string;
  dispatch: React.Dispatch<Action>;
  onDelete: () => void;
}

const MedicationEditor: React.FC<Props> = ({ medication, state, hospital, dispatch, onDelete }) => {
  const [protocolsModalOpen, setProtocolsModalOpen] = useState(false);

  const usedInProtocols = useMemo(
    () => getProtocolsForMedication(state.data, medication.id),
    [state.data, medication.id]
  );

  const sectionsForThisMed = useMemo(
    () => state.data.drugSections.filter(s => s.medicationIds.includes(medication.id)).map(s => s.name),
    [state.data.drugSections, medication.id]
  );

  const updateMedication = <K extends keyof DrugRecord & keyof DripRecord>(field: K, value: any) => {
    dispatch({ type: 'UPDATE_MEDICATION', id: medication.id, patch: { [field]: value } });
  };

  const updateDrugField = (field: keyof DrugRecord, value: any) => {
    dispatch({ type: 'UPDATE_MEDICATION', id: medication.id, patch: { [field]: value } as Partial<DrugRecord> });
  };

  const updateDripField = (field: keyof DripRecord, value: any) => {
    dispatch({ type: 'UPDATE_MEDICATION', id: medication.id, patch: { [field]: value } as Partial<DripRecord> });
  };

  const onToggleProtocolAssignment = (protocolId: string) => {
    dispatch({ type: 'TOGGLE_ASSIGNMENT', protocolId, medicationId: medication.id });
  };

  const onToggleSection = (sectionName: string) => {
    dispatch({ type: 'TOGGLE_MEDICATION_IN_DRUG_SECTION', medicationId: medication.id, sectionName });
  };

  return (
    <form className="resus-admin__form" onSubmit={(e) => e.preventDefault()}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 style={{ margin: 0 }}>
            {medication.name || (medication.kind === 'drug' ? 'תרופה חדשה' : 'דריפ חדש')}
          </h2>
          <span className="resus-admin__subtle">
            {medication.kind === 'drug' ? 'תרופה' : 'דריפ'} · מזהה: <code>{medication.id}</code>
          </span>
        </div>
        <button type="button" className="resus-admin__btn resus-admin__btn--danger" onClick={onDelete}>
          מחק
        </button>
      </div>

      {/* Common fields */}
      <div className="resus-admin__field-row">
        <div className="resus-admin__field">
          <label className="resus-admin__field-label">שם התרופה</label>
          <input
            className="resus-admin__input"
            value={medication.name}
            onChange={(e) => updateMedication('name', e.target.value)}
          />
        </div>
        <div className="resus-admin__field">
          <label className="resus-admin__field-label">דרך מתן (howToGive)</label>
          <input
            className="resus-admin__input"
            value={medication.howToGive ?? ''}
            placeholder="IV, IM, INH, PO..."
            onChange={(e) => updateMedication('howToGive', e.target.value || null)}
          />
        </div>
      </div>

      <div className="resus-admin__field">
        <label className="resus-admin__field-label">הערה כללית (תופיע בכל פרוטוקול)</label>
        <p className="resus-admin__field-hint">
          להערות ספציפיות לפרוטוקול עברו לשלב 3.
        </p>
        <textarea
          className="resus-admin__textarea"
          rows={2}
          value={medication.comment ?? ''}
          onChange={(e) => updateMedication('comment', e.target.value || null)}
        />
      </div>

      <div className="resus-admin__field">
        <label className="resus-admin__field-label">אזהרה (warnText) — בצבע אדום</label>
        <input
          className="resus-admin__input"
          value={medication.warnText ?? ''}
          onChange={(e) => updateMedication('warnText', e.target.value || null)}
        />
      </div>

      {medication.kind === 'drug' ? (
        <DrugFields drug={medication as DrugRecord} onChange={updateDrugField} />
      ) : (
        <DripFields drip={medication as DripRecord} onChange={updateDripField} />
      )}

      {/* Cases (conditional dosing) */}
      <div>
        <h3 style={{ marginBottom: 8 }}>תנאי משקל / גיל</h3>
        <p className="resus-admin__field-hint">
          לתרופות שהמינון שלהן משתנה לפי משקל או גיל (למשל פניטואין). אם השדות הסטנדרטיים מספיקים — השאירו ריק.
        </p>
        <CasesEditor
          cases={medication.cases ?? null}
          kind={medication.kind}
          onChange={(cases) => dispatch({ type: 'UPDATE_MEDICATION_CASES', id: medication.id, cases })}
        />
      </div>

      {/* Drug categories (drugSections) */}
      <div className="resus-admin__field">
        <label className="resus-admin__field-label">שיוך לקטגוריות תרופות (לעיון במסך התרופות)</label>
        <p className="resus-admin__field-hint">
          אלה הקבוצות במסך "תרופות חדר הלם" המאפשרות גלישה לפי תחום קליני.
        </p>
        <div className="resus-admin__chips">
          {state.data.drugSections.map(section => {
            const active = sectionsForThisMed.includes(section.name);
            return (
              <button
                key={section.name}
                type="button"
                className={`resus-admin__chip${active ? ' resus-admin__chip--active' : ''}`}
                onClick={() => onToggleSection(section.name)}
              >
                {section.name}
              </button>
            );
          })}
          {state.data.drugSections.length === 0 && (
            <span className="resus-admin__subtle">לא נטענו קטגוריות מברירת המחדל.</span>
          )}
        </div>
      </div>

      {/* Used in protocols */}
      <div className="resus-admin__field">
        <label className="resus-admin__field-label">בשימוש בפרוטוקולים</label>
        <p className="resus-admin__field-hint">
          סדר וההערות הספציפיות מוגדרים בשלב 3.
        </p>
        <div className="resus-admin__chips">
          {usedInProtocols.length === 0 && (
            <span className="resus-admin__subtle">לא משוייכת לפרוטוקול.</span>
          )}
          {usedInProtocols.map(p => (
            <span key={p.id} className="resus-admin__chip">{p.name || p.id}</span>
          ))}
          <button
            type="button"
            className="resus-admin__btn resus-admin__btn--ghost"
            onClick={() => setProtocolsModalOpen(true)}
            style={{ padding: '4px 10px', fontSize: '0.85rem' }}
          >
            ניהול שיוך מהיר
          </button>
        </div>
      </div>

      {/* Live preview */}
      <MedicationPreview
        medication={medication}
        hospital={hospital}
        preset={state.ui.sampleWeightPreset}
        onPresetChange={(preset) => dispatch({ type: 'SET_PRESET', preset })}
      />

      {protocolsModalOpen && (
        <ManageProtocolsModal
          protocols={state.data.protocols}
          selectedIds={new Set(usedInProtocols.map(p => p.id))}
          onToggle={onToggleProtocolAssignment}
          onClose={() => setProtocolsModalOpen(false)}
        />
      )}
    </form>
  );
};

// --- Drug-specific fields ------------------------------------------------------

interface DrugFieldsProps {
  drug: DrugRecord;
  onChange: (field: keyof DrugRecord, value: any) => void;
}

const DrugFields: React.FC<DrugFieldsProps> = ({ drug, onChange }) => {
  return (
    <fieldset style={{ border: '1px solid #DDE5EE', borderRadius: 8, padding: 16 }}>
      <legend>חישוב מינון</legend>

      <div className="resus-admin__field" style={{ marginBottom: 12 }}>
        <label className="resus-admin__field-label">type — סוג חישוב</label>
        <p className="resus-admin__field-hint">
          ‎"mass" = מינון במשקל (mg, units...). ‎"fluid" = בולוס נוזלים. ריק = מינון בנפח לפי ריכוז.
        </p>
        <div className="resus-admin__chips">
          {[
            { val: null as DrugType, label: 'volume by concentration' },
            { val: 'mass' as DrugType, label: 'mass' },
            { val: 'fluid' as DrugType, label: 'fluid' }
          ].map(opt => (
            <button
              key={String(opt.val)}
              type="button"
              className={`resus-admin__chip${drug.type === opt.val ? ' resus-admin__chip--active' : ''}`}
              onClick={() => onChange('type', opt.val)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
        <NumberField
          label="dose_per_kg (מינון לקילו)"
          value={drug.dose_per_kg}
          onChange={(n) => onChange('dose_per_kg', n)}
        />
        <NumberField
          label="constant_dose (מינון קבוע, לא לפי משקל)"
          value={drug.constant_dose}
          onChange={(n) => onChange('constant_dose', n)}
        />
        <TextField
          label="dose_unit (יחידה)"
          value={drug.dose_unit ?? ''}
          onChange={(s) => onChange('dose_unit', s || null)}
          placeholder="mg, ml, mcg, gr, units, mmol..."
        />
        <TextField
          label="dose_range — תיאור טווח מותר (אופציונלי)"
          value={drug.dose_range ?? ''}
          onChange={(s) => onChange('dose_range', s || null)}
          placeholder='למשל: "mg/kg(0.5-3)"'
        />
        <TextField
          label="concentration (numerator/denominator)"
          value={drug.concentration ?? ''}
          onChange={(s) => onChange('concentration', s || null)}
          placeholder='למשל: "1/10" = 1mg ב-10ml'
        />
        <TextField
          label="maxDose"
          value={drug.maxDose ?? ''}
          onChange={(s) => onChange('maxDose', s || null)}
        />
        <TextField
          label="minDose"
          value={drug.minDose ?? ''}
          onChange={(s) => onChange('minDose', s || null)}
        />
        <TextField
          label="minDoseUnit (יחידת minDose)"
          value={drug.minDoseUnit ?? ''}
          onChange={(s) => onChange('minDoseUnit', s || null)}
        />
        <CheckboxField
          label="הצג את הריכוז למשתמש (shouldDispalyConcentration)"
          checked={!!drug.shouldDispalyConcentration}
          onChange={(b) => onChange('shouldDispalyConcentration', b)}
        />
        <TextField
          label="administrationInstructions"
          value={drug.administrationInstructions ?? ''}
          onChange={(s) => onChange('administrationInstructions', s || null)}
        />
      </div>
    </fieldset>
  );
};

// --- Drip-specific fields ------------------------------------------------------

interface DripFieldsProps {
  drip: DripRecord;
  onChange: (field: keyof DripRecord, value: any) => void;
}

const DripFields: React.FC<DripFieldsProps> = ({ drip, onChange }) => {
  const showDilution = drip.calc_type === 'DilutionInstructions';
  const showInfusion = drip.calc_type === 'InfusionSpeed';

  return (
    <fieldset style={{ border: '1px solid #DDE5EE', borderRadius: 8, padding: 16 }}>
      <legend>הגדרות דריפ</legend>

      <div className="resus-admin__field" style={{ marginBottom: 12 }}>
        <label className="resus-admin__field-label">calc_type — סוג חישוב</label>
        <p className="resus-admin__field-hint">
          DilutionInstructions: לדלל לנפח יעד.{' '}
          InfusionSpeed: ריכוז ידוע — חישוב מהירות הזלפה בלבד.
        </p>
        <div className="resus-admin__chips">
          {[
            { val: 'DilutionInstructions' as DripCalcType, label: 'DilutionInstructions' },
            { val: 'InfusionSpeed' as DripCalcType, label: 'InfusionSpeed' }
          ].map(opt => (
            <button
              key={String(opt.val)}
              type="button"
              className={`resus-admin__chip${drip.calc_type === opt.val ? ' resus-admin__chip--active' : ''}`}
              onClick={() => onChange('calc_type', opt.val)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
        <TextField
          label="dose_unit"
          value={drip.dose_unit ?? ''}
          onChange={(s) => onChange('dose_unit', s || null)}
          placeholder="mcg, mg, mmol, units..."
        />
        <TextField
          label="allowed_dose_range (תיאור טווח)"
          value={drip.allowed_dose_range ?? ''}
          onChange={(s) => onChange('allowed_dose_range', s || null)}
          placeholder='למשל: "0.05-1 mcg/kg/min"'
        />
        <NumberField
          label="dose_per_kg_per_min"
          value={drip.dose_per_kg_per_min}
          onChange={(n) => onChange('dose_per_kg_per_min', n)}
        />
        <NumberField
          label="dose_per_kg_per_hour"
          value={drip.dose_per_kg_per_hour}
          onChange={(n) => onChange('dose_per_kg_per_hour', n)}
        />

        {showDilution && (
          <>
            <NumberField
              label="default_dilution_volume_ml"
              value={drip.default_dilution_volume_ml}
              onChange={(n) => onChange('default_dilution_volume_ml', n)}
            />
            <TextField
              label="default_dilution_volume_unit"
              value={drip.default_dilution_volume_unit ?? ''}
              onChange={(s) => onChange('default_dilution_volume_unit', s || null)}
              placeholder="ml"
            />
            <NumberField
              label="target_volume_ml_per_hour"
              value={drip.target_volume_ml_per_hour}
              onChange={(n) => onChange('target_volume_ml_per_hour', n)}
            />
          </>
        )}

        {showInfusion && (
          <>
            <TextField
              label="existing_dilution_concentration (numerator/denominator)"
              value={drip.existing_dilution_concentration ?? ''}
              onChange={(s) => onChange('existing_dilution_concentration', s || null)}
              placeholder='למשל: "40/1000" = 40 mmol ב-1000 ml'
            />
            <TextField
              label="existing_dilution_concentration_dose_unit"
              value={drip.existing_dilution_concentration_dose_unit ?? ''}
              onChange={(s) => onChange('existing_dilution_concentration_dose_unit', s || null)}
              placeholder="mmol, mg, units..."
            />
          </>
        )}

        <NumberField
          label="max_dose_per_hour"
          value={drip.max_dose_per_hour}
          onChange={(n) => onChange('max_dose_per_hour', n)}
        />
      </div>
    </fieldset>
  );
};

// --- Field primitives ----------------------------------------------------------

const NumberField: React.FC<{
  label: string;
  value: number | null | undefined;
  onChange: (val: number | null) => void;
}> = ({ label, value, onChange }) => (
  <div className="resus-admin__field">
    <label className="resus-admin__field-label">{label}</label>
    <input
      className="resus-admin__input"
      type="number"
      step="any"
      value={value === null || value === undefined ? '' : value}
      onChange={(e) => {
        if (e.target.value === '') onChange(null);
        else onChange(Number(e.target.value));
      }}
    />
  </div>
);

const TextField: React.FC<{
  label: string;
  value: string;
  placeholder?: string;
  onChange: (val: string) => void;
}> = ({ label, value, placeholder, onChange }) => (
  <div className="resus-admin__field">
    <label className="resus-admin__field-label">{label}</label>
    <input
      className="resus-admin__input"
      type="text"
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

const CheckboxField: React.FC<{
  label: string;
  checked: boolean;
  onChange: (val: boolean) => void;
}> = ({ label, checked, onChange }) => (
  <label className="resus-admin__field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
    <span className="resus-admin__field-label" style={{ fontWeight: 400 }}>{label}</span>
  </label>
);

export default MedicationEditor;
